import _get from 'lodash/get';
import { LAYOUT_ENTITIES } from '@bedrock/fido-sdk';
import { ISO693_TO_3 } from '../../../lang/languageNames/ISO693-1to3.constants';
import { getOriginalTarget, getReason } from '../lock/lock.utils';
import { PAGE_STATE } from '../../modules/tornado/utils/virtualization';

export * from './customer/default/player.utils';

const DRM_SOFTWARE = 'software';
const DRM_HARDWARE = 'hardware';

// Index of the next playable source at or after `startIndex` (or -1); pass currentSourceIndex + 1 for a fallback.
export const selectNextPlayableSource = ({ sources, hasHardwareDRMSupport = false, startIndex = 0 }) =>
  (sources || []).findIndex((source, index) => {
    if (index < startIndex || !source) {
      return false;
    }

    const { format, container } = source;

    // Non-protected types: regular MPD, MP3 files (podcasts), and radio (HLS)
    if (format === 'dash' || format === 'mp3' || (format === 'hls' && container === 'aac')) {
      return true;
    }

    // All other types are not supported on the boxes
    if (format !== 'dashcenc') {
      return false;
    }

    const drmType = _get(source, 'drm.type', '').toLowerCase();
    if (hasHardwareDRMSupport) {
      return drmType === DRM_HARDWARE;
    }

    return drmType === DRM_SOFTWARE;
  });

export const findPlayerBlock = blocks => {
  if (!blocks) {
    return null;
  }

  return blocks.find(({ templateId }) => templateId === 'Player');
};

export const findCurrentVideoItem = (layout, entityId) => {
  const blocks = _get(layout, 'blocks', []);
  const block = findPlayerBlock(blocks);

  return _get(block, 'content.items', []).find(item => _get(item, 'video.id', '') === entityId);
};

export const findSideGlassBlock = layout => {
  const blocks = (layout && layout.blocks) || [];

  const result = blocks.find(block => {
    if (!block) {
      return false;
    }

    return block.blockTemplateId === 'SideGlass';
  });

  if (!result && blocks.length > 1) {
    return blocks[1];
  }

  return result;
};

/**
 * Maps every page of a paginated content to its loading state
 *
 * @param {number} totalItems amount of the items within the whole block
 * @param {number} itemsPerPage amount of the items within a single page
 * @param {Object[]} items already loaded items
 * @returns {Object} loading state, per zero-based page index
 */
export const getPagesState = (totalItems, itemsPerPage, items = []) => {
  const pages = {};

  if (!totalItems || !itemsPerPage) {
    return pages;
  }

  for (let start = 0; start < totalItems; start += itemsPerPage) {
    const end = Math.max(Math.min(start + itemsPerPage, totalItems) - 1, 0);

    pages[start / itemsPerPage] = items[start] && items[end] ? PAGE_STATE.LOADED : PAGE_STATE.MISSING;
  }

  return pages;
};

/**
 * Seeds the pagination state of the block displayed within the side glass panel, as the block
 * is paginated within the player state, apart from the layout cache
 *
 * @param {Object} block side glass block, as retrieved from the layout
 * @returns {Object} the block holding its pages state
 */
export const prepareSideGlassBlock = block => {
  if (!block) {
    return block;
  }

  const { items = [], pagination } = block.content || {};
  const { totalItems, itemsPerPage } = pagination || {};

  return {
    ...block,
    pages: getPagesState(totalItems, itemsPerPage, items),
  };
};

export const findVideoItemImageIdByRole = (videoItem, role) => _get(videoItem, `video.images.${role}.id`, '');

export const findJingleImageId = videoItem => findVideoItemImageIdByRole(videoItem, 'jingle');

export const findAdJingleImageId = videoItem => findVideoItemImageIdByRole(videoItem, 'ad_jingle');

export const findVideoItemProgramImageId = videoItem => _get(videoItem, 'image.id', '');

export const isPlayerLayout = layout => {
  const { type } = layout ? layout.entity : {};

  return (
    type === LAYOUT_ENTITIES.video ||
    type === LAYOUT_ENTITIES.live ||
    type === LAYOUT_ENTITIES.audio ||
    type === LAYOUT_ENTITIES.radio
  );
};

export const toISO693LanguageCode = code => {
  if (!code) {
    return '';
  }

  if (code.length === 3) {
    return code;
  }

  return ISO693_TO_3[code] || '';
};

export const parseTrack = track =>
  track
    ? {
        ...track,
        language: toISO693LanguageCode(track.language),
      }
    : null;

export const isForcedSubtitlesTrack = track =>
  Array.isArray(track.roles) ? track.roles.includes('forced_subtitle') : false;

export const findForcedSubtitles = (tracks, language) =>
  tracks.filter(isForcedSubtitlesTrack).find(track => track.language === language);

export const findPreferredAudioTrack = (tracks, preference) =>
  preference.audioDescription
    ? tracks.find(
        ({ language, roles }) =>
          Array.isArray(roles) && roles.includes('alternate') && language === preference.language,
      )
    : tracks.find(({ language }) => language === preference.language);

export const findMatchingAudioTrack = (tracks, preference, originalLanguage) => {
  if (!tracks) {
    return null;
  }

  return (
    (preference && findPreferredAudioTrack(tracks, preference)) ||
    tracks.find(({ language }) => language === originalLanguage) ||
    tracks[0]
  );
};

export const findMatchingSubtitlesTrack = (tracks, preference) => {
  if (!tracks || !preference) {
    return null;
  }

  return preference.role
    ? tracks.find(({ language, roles }) => {
        if (language !== preference.language || !Array.isArray(roles)) {
          return false;
        }

        switch (preference.role) {
          case 'sdh':
            return roles.includes('caption');
          case 'standard':
            // NOTE: subtitle role should be an exact match
            return roles.length === 1 && roles.includes('subtitle');
          default:
            return false;
        }
      })
    : tracks.find(({ language }) => language === preference.language);
};

/**
 * @typedef {Object} AudioPreference
 * @property {string} audioLanguage ISO639-3 locale code
 * @property {boolean} audioDescription true if the track is a descriptive audio
 */

/**
 * @typedef {Object} SubtitlesPreference
 * @property {string} subtitleLanguage ISO639-3 locale code
 * @property {boolean} role subtitles role: standard, CC, etc. Comes from manifest (?)
 */

/**
 * @typedef {Object} PlayerTrack
 * @property {string} language ISO639-1 (potentially) locale code
 * @property {Array<string>} roles track roles defined in manifest
 */

/**
 * @param {Object} track audio track data from BFF response
 * @param {string} track.language ISO639-3 locale code
 * @param {boolean} track.audioDescription true if the track is a descriptive audio
 * @return {AudioPreference}
 */
export const layoutAudioTrackToUsersPreference = track => ({
  audioLanguage: ISO693_TO_3[track.language] || track.language,
  audioDescription: track.audioDescription || false,
});

/**
 * @param {Object} track audio track data from BFF response
 * @param {string} track.language ISO639-3 locale code
 * @param {boolean} track.role subtitles role
 * @return {SubtitlesPreference}
 */
export const layoutTextTrackToUsersPreference = track => ({
  subtitleLanguage: ISO693_TO_3[track.language] || track.language,
  role: track.role || 'standard',
});

/**
 * Converts player audio track (comes from DASH) into User API audio track preferences
 *
 * @param {PlayerTrack} track
 * @returns {AudioPreference}
 */
export const audioTrackToPreference = track => ({
  language: ISO693_TO_3[track.language] || track.language,
  audioDescription: track.roles ? track.roles.includes('alternate') : false,
});

/**
 * Converts player subtitles track (comes from DASH) into User API subtitles preferences
 *
 * @param {PlayerTrack} track
 * @returns {SubtitlesPreference}
 */
export const subtitlesTrackToPreference = track => ({
  language: ISO693_TO_3[track.language] || track.language,
  // See https://bedrock.tpondemand.com/entity/195625-brrdk22-track-preferences-cross-devices-info/comments/249273
  role: track.roles && track.roles.includes('caption') ? 'sdh' : 'standard',
});

export const audioPreferenceToManifestTrack = preference =>
  preference
    ? {
        language: preference.language,
        role: preference.audioDescription ? 'alternate' : 'main',
      }
    : undefined;

export const textPreferenceToManifestTrack = preference =>
  preference
    ? {
        language: preference.language,
        role: preference.role === 'sdh' ? 'caption' : 'main',
      }
    : undefined;

export const removeParentalLock = items =>
  items.map(item => {
    const wrapper = { item };
    const originalTarget = getReason(wrapper) === 'parentalCode' && getOriginalTarget(wrapper);
    if (!originalTarget) {
      return item;
    }

    return {
      ...item,
      action: {
        ...item.action,
        target: originalTarget,
      },
    };
  });
